// usePaulinaVersionControls — gestión de VERSIÓN de la feature agent-chat (Paulina).
//
// El contrato FeatureGate (useAgentChatGate) solo expone la INVITACIÓN
// (offered/inviteEligible/showInvite/shown/accept/dismiss). El revert + feedback viven en
// `useFeatureVersion(featureKey)` directamente — mismo reparto que la feature de referencia
// (impresion-ble: invitación por el gate; revert/feedback por useFeatureVersion en
// BlePrintingVersionControls/BlePrintingRolloutOverlays). Este hook empaqueta ESA segunda
// mitad para Paulina:
//   - `deactivate` → la acción de revert('revert_commit') del rollout (vuelve a 'control':
//     Paulina deja de montarse vía PaulinaGate.offered) + el prompt de feedback (signal de
//     módulo, renderizado a nivel host por PaulinaRolloutOverlays — la superficie que lo pide se
//     desmonta con el revert optimista). Es `null` cuando NO se puede ofrecer: así el host no
//     tiene un booleano que olvidar consultar, simplemente no hay función que llamar.
//   - `submitRevertFeedback`/`feedbackOptions` → para el prompt de motivos.
//
// El gate del BACKEND (feature graduada a GA o sin can_revert) lo entrega el contrato compartido
// `useFeatureVersionControls` (useFeatureGate.ts) — la misma pieza que consumen impresion-ble y
// menu-v2. El ROL se decide AQUÍ y no en el contrato: Paulina OCULTA el control al no-root,
// mientras menu-v2 lo muestra y responde con aviso suave/vista previa (ver el bloque "GESTIÓN DE
// VERSIÓN" del contrato). La defensa REQUIRES_ROOT sigue por detrás (el outbox revierte el
// optimista si el backend rechaza).
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { useFeatureVersionControls } from '@hooks/useFeatureGate';
import { useEffectiveRoot } from '@hooks/useEffectiveRoot';
import { AGENT_CHAT_FEATURE_KEY } from '@utils/featureRollout/constants';
import { requestRevertFeedback } from '@utils/featureRollout/revertFeedback';
import type { FeedbackOptionsCatalog } from '@utils/featureRollout/types';

export interface PaulinaVersionControls {
  /**
   * Ejecuta el revert del rollout (Paulina deja de montarse) y pide el prompt de feedback.
   * `null` = no se puede ofrecer "Desactivar Paulina" (no-root, rol sin resolver, o la feature
   * ya no es gestionable según el contrato).
   */
  deactivate: (() => void) | null;
  /** Catálogo de chips per-experimento (ya normalizado) o null → fallback a strings.ts. */
  feedbackOptions: FeedbackOptionsCatalog | null;
  /** Envía el motivo del revert como telemetría fire-and-forget (behavior 'revert_feedback'). */
  submitRevertFeedback: (reason: string, text?: string, trigger?: string) => void;
}

export const usePaulinaVersionControls = (): PaulinaVersionControls => {
  const { submitRevertFeedback, feedbackOptions } = useFeatureVersion(AGENT_CHAT_FEATURE_KEY);
  const versionControls = useFeatureVersionControls(AGENT_CHAT_FEATURE_KEY);
  const effectiveRoot = useEffectiveRoot(AGENT_CHAT_FEATURE_KEY);

  // Solo el root efectivo gestiona la versión del comercio; rol sin resolver (undefined) → no
  // ofrecer el control. El resto del gate (can_revert / GA) viene ya resuelto del contrato.
  const deactivate =
    effectiveRoot && versionControls
      ? () => {
          // Orden deliberado (igual que BLE): primero el revert del rollout (optimista: Paulina
          // deja de montarse YA y el PUT viaja por el outbox), luego pedimos el prompt de
          // feedback vía signal de módulo (sobrevive al desmontaje de la superficie que lo pidió).
          versionControls.revert('revert_commit');
          requestRevertFeedback(AGENT_CHAT_FEATURE_KEY);
        }
      : null;

  return { deactivate, feedbackOptions, submitRevertFeedback };
};
